import styled, { css, keyframes } from 'styled-components'

import { _XS_Medium, _XS_Normal, mainColors, semanticColors } from '@avantstay/backoffice-core'

import {
  Bubble as BaseBubble,
  ErrorBanner as BaseErrorBanner,
  InputWrapper as BaseInputWrapper,
  Root as BaseRoot,
} from '@new/__ui/AIChat/AIChat.styles'

// Common chat language comes from the shared AI chat building blocks;
// re-exported so the component keeps a single style namespace.
export {
  AvatarCircle as Avatar,
  InputArea,
  InputBar,
  MessageList,
  MessageRow,
  SendButton,
  Sheen,
} from '@new/__ui/AIChat/AIChat.styles'

// Remy answers link to Voyage docs/screens; style them in the brand colors
// (the shared bubble has no link styling).
export const Bubble = styled(BaseBubble)`
  & a {
    color: ${mainColors.primaryMedium};
    text-decoration: none;
  }
  & a:hover {
    color: ${mainColors.primaryDark};
    text-decoration: underline;
  }
`

export const ErrorBanner = styled(BaseErrorBanner)`
  margin: 0 20px 12px;
`

// ---- Toolbar (new chat / past chats)

export const ChatToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 16px;
  border-bottom: 1px solid ${mainColors.neutralMedium};
  background: ${mainColors.neutralExtraLight};
`

export const ToolbarButton = styled.button`
  ${_XS_Medium}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid ${mainColors.neutralMedium};
  background: ${mainColors.white};
  color: ${mainColors.neutralExtremelyDark2};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${mainColors.accentLight};
    color: ${mainColors.accentDark};
    background: ${mainColors.accentExtremelyLight};
  }

  svg {
    flex-shrink: 0;
  }
`

// Icon-only toolbar action (user guide); same visual language as ToolbarButton.
export const ToolbarIconButton = styled(ToolbarButton)`
  padding: 5px 7px;
`

// ---- Empty state

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin: auto;
  padding: 40px 20px;
  color: ${mainColors.neutralExtraDark};
`

// ---- Transient Remy progress steps (data-remy-step) shown while the agent works.

export const StepList = styled.ul`
  ${_XS_Normal}
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${mainColors.neutralExtraDark};
`

const stepPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.15); }
`

export const StepItem = styled.li<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ $active }) =>
    $active ? mainColors.neutralExtremelyDark2 : mainColors.neutralExtraDark};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $active }) =>
      $active ? mainColors.accentMedium : semanticColors.successMedium};
    ${({ $active }) =>
      $active &&
      css`
        animation: ${stepPulse} 1s ease-in-out infinite;
      `}
  }
`

// ---- Message attachments (chips above the textarea, whole-panel drop zone)

// Positioning context for the drop overlay.
export const Root = styled(BaseRoot)`
  position: relative;
`

// Chips stack above the textarea row, so the wrapper turns into a column.
export const InputWrapper = styled(BaseInputWrapper)`
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`

export const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
`

export const AttachButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 32px;
  height: 32px;
  margin-left: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${mainColors.neutralExtraDark};
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${mainColors.neutralLight};
    color: ${mainColors.neutralExtremelyDark2};
  }
`

// Visually hidden (not display:none) so the input stays focusable/testable.
export const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  clip-path: inset(50%);
  overflow: hidden;
  white-space: nowrap;
`

export const AttachmentError = styled.p`
  ${_XS_Normal}
  margin: 0;
  width: 100%;
  color: ${semanticColors.failureDark};
`

// Files that went out with a sent message, listed under the bubble text.
export const BubbleFileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
`

export const BubbleFile = styled.span`
  ${_XS_Normal}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.85;

  svg {
    flex-shrink: 0;
  }
`

// Clickable variant: sent files whose payload is still in message state can be
// re-downloaded. Inherits the bubble text color so it works on either role.
export const BubbleFileButton = styled.button`
  ${_XS_Normal}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  opacity: 0.85;
  cursor: pointer;
  text-align: left;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
  }
`

// ---- Resize handles

// Full-width grab strip at the top of the input bar (over its padding) that
// drag-resizes the textarea vertically. The pill only shows on hover/drag.
export const InputResizeHandle = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  margin: -12px -20px 0;
  cursor: row-resize;
  touch-action: none;

  &::after {
    content: '';
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: ${({ $active }) => ($active ? mainColors.accentMedium : 'transparent')};
    transition: background-color 0.15s ease;
  }

  &:hover::after {
    background: ${({ $active }) => ($active ? mainColors.accentMedium : mainColors.neutralMedium)};
  }
`

export const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Drag events must keep landing on the panel root underneath. */
  pointer-events: none;
  margin: 8px;
  border: 2px dashed ${mainColors.accentMedium};
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.85);
  color: ${mainColors.accentDark};
  font-weight: 600;
`

// ---- Helpful / not-helpful feedback on assistant answers

// Column wrapper so the feedback row sits under the assistant bubble while the
// pair still occupies the bubble's slot in the message row. The width cap
// moves here so the inner bubble can fill it.
export const AssistantBubbleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  max-width: 78%;

  ${Bubble} {
    max-width: 100%;
  }
`

export const FeedbackRow = styled.div`
  display: flex;
  gap: 2px;
  padding-left: 4px;
`

export const FeedbackButton = styled.button<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ $selected }) => ($selected ? mainColors.accentDark : mainColors.neutralExtraDark)};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${mainColors.neutralLight};
    color: ${mainColors.neutralExtremelyDark2};
  }

  &:disabled {
    cursor: default;
    opacity: ${({ $selected }) => ($selected ? 1 : 0.35)};
  }
`

// Container for approval cards rendered in place of the assistant bubble;
// sized like the bubble so the card aligns with regular messages.
export const ApprovalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 78%;
  flex: 1;
`
